/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import TreeNode from "App/utils/globe/chunk_lod/tree/node";
import PatchManager from "App/utils/globe/chunk_lod/patch_manager";
import {mat4, vec3} from "gl-matrix";
import DataHandler from "App/utils/globe/chunk_lod/data/data_handler";
import IAddress from "App/utils/globe/chunk_lod/adress";
import BoundingBox from "App/utils/bounding_box";

export enum ChunkLODNodeState {
    CREATED = 0,
    DATAREQUESTED = 1,
    FAILED = 2,
    DATAREADY = 3,
    COMMITTEDTOGPU = 4,
    SETTLEDONGPU = 5,
    REMOVED = 6
}

/**
 * Abstract class for implementing the tree structure
 *
 * @param aParent
 * @param aAddress
 * @constructor
 */
export default
class ChunkLODNode extends TreeNode {

    // prevent memory allocation
    private static viewMatrix: mat4 = mat4.create();
    private static projectionMatrix: mat4 = mat4.create();
    private static viewProjectionMatrix: mat4 = mat4.create();

    private static cameraDirection: vec3 = vec3.create();
    private static toNodeDirection: vec3 = vec3.create();

    protected _patch: PatchManager;
    protected _address: IAddress;
    private _size: number;
    protected _center: vec3;
    protected _bbox: BoundingBox;
    private _state: ChunkLODNodeState;

    constructor(aPatch: PatchManager, aParent: ChunkLODNode, aAddress: IAddress) {
        super(aParent, aAddress.patchInfo.nc);

        this._patch = aPatch;
        this._address = aAddress;
        this._UID = aAddress.generateUID();

        this._size = this._address.getSize();
        this._center = this._address.getCenter();
        let top = this._patch.getTops()[this._address.flattenedTreeIndex];
        let bottom = this._patch.getBottoms()[this._address.flattenedTreeIndex];
        this._center[1] = bottom + (top - bottom) / 2;

        this._bbox = new BoundingBox(vec3.fromValues(
                this._center[0] - this._size / 2,
                bottom,
                this._center[2] - this._size / 2
            ), vec3.fromValues(
                this._center[0] + this._size / 2,
                top,
                this._center[2] + this._size / 2
            )
        );

        this._state = ChunkLODNodeState.CREATED;

        this._data = null;
    }

    public getCenter(): vec3 {
        return this._center;
    }

    public getAttributes(aAddress: IAddress, aAttributes: any) {
        return this._data.getAttributes();
    }

    public hasValidDataPath(): boolean {
        if (this.getParent()) {
            return this._data && (this.getParent()).hasValidDataPath();
        }

        return this._data !== null;
    }

    public getIndices() {
        return this._data.getIndices();
    }

    public getMetadata() {
        return this._data.getMetadata();
    }

    public getTextureData() {
        return this._data.getTextureData();
    }

    public setData(aData: any) {
        this._data = new DataHandler(this);
        this._data.fill(aData);

        // tweak bbox top and bottom to what is stored in metadata
        this._bbox = new BoundingBox(this._data.getMetadata().bbox.min, this._data.getMetadata().bbox.max);

        this._center = vec3.clone(this._bbox.getCenter());
    }

    public getData() {
        return this._data;
    }

    public setState(aState: ChunkLODNodeState) {
        this._state = aState;
    }

    public getState(): ChunkLODNodeState {
        return this._state;
    }

    public getAddress(): IAddress {
        return this._address;
    }

    public createChild(aIndex: number) {
        let address = this._address.create({
            base: this._address.base,
            origin: this._address.origin,
            patchInfo: this._address.patchInfo,
            level: this._address.level + 1,
            layer: this._address.layer * 2 + Math.floor((aIndex / 4) + 0.1),
            row: this._address.row * 2 + Math.floor(((aIndex % 4) / 2) + 0.1), // %4 is to handle octree
            col: this._address.col * 2 + (aIndex % 2),
            translationOffset: this._address.translationOffset
        });

        this._children[aIndex] = new ChunkLODNode(this._patch, this, address);

        return this._children[aIndex];
    }

    public testAllChildrenHasState(aState: ChunkLODNodeState): boolean {
        for (let i = 0; i < this._children.length; i++) {
            if (this._children[i] === null || (this._children[i]).getState() !== aState) {
                return false;
            }
        }

        return true;
    }

    public getRequireDataForChildren(aView: any): boolean {

        if (this._state === ChunkLODNodeState.FAILED) {
            return false;
        }

        // we just place the camera in origin looking at target at distance from origin to the tile's center
        // the far value is placed in the centre and the near value is computed so the whole tile is in the frustum
        let distance = vec3.distance(aView.eye, this._center);

        let height = 2 * Math.tan(aView.fov / 2) * distance; // visible height
        let requiredPixelsOnScreen = (this._size / height) * Math.max(aView.vpsize[0], aView.vpsize[1]);

        let isMobile = this._patch.getWorker().getIsMobile();

        if (aView.viewFrustum.intersect(this._bbox, 0) === -1) {
            if (isMobile) {
                requiredPixelsOnScreen = 0;
            } else {
                requiredPixelsOnScreen /= 2; // by default there should be zero to be aggressive
            }
        }

        // we need better detail even in more far tiles so for the coarsest three levels we increase their required pixels on screen by some factor
        if (!isMobile) {
            let level = this._address.getLevel();
            if (level <= 2) {
                requiredPixelsOnScreen *= Math.pow(2, 3 - level);
            }
        }

        return requiredPixelsOnScreen > (512 / (Math.pow(4, this._address.patchInfo.nc / 8))) && this._address.getLevel() < (this._address.patchInfo.levels - 1);
    }
}
